import { Stack } from 'expo-router';

import { useAnalysisResult } from '../../hooks/useAnalysisResult';
import { SharedLinkContent } from './SharedLinkContent';
import { SharedLinkError } from './SharedLinkError';
import { SharedLinkHeader } from './SharedLinkHeader';
import { SharedLinkLoading } from './SharedLinkLoading';
import type { QuotaTier } from '../trial/QuotaExhaustedModal';

type AnalysisData = {
  title?: string;
  description?: string;
  score?: number;
} | null;

interface LegacySharedLinkContentProps {
  shortId: string;
  validatedAnalysisId: string | null;
  analysisData: AnalysisData;
  currentTier: QuotaTier;
  displayTier: QuotaTier;
  tierLevel: number;
  l3AccordionsEnabled: boolean;
  bottomPadding: number;
  onNewAnalysis: () => void;
  onUpgrade: () => void;
  t: unknown;
}

/**
 * Legacy links retain the identifier-backed analysis path. Keeping it in a
 * separate component ensures explicit public results cannot invoke that hook.
 */
export function LegacySharedLinkContent({
  shortId,
  validatedAnalysisId,
  analysisData,
  currentTier,
  displayTier,
  tierLevel,
  l3AccordionsEnabled,
  bottomPadding,
  onNewAnalysis,
  onUpgrade,
  t,
}: LegacySharedLinkContentProps) {
  const { data: analysis, isLoading, error: analysisError } = useAnalysisResult(
    validatedAnalysisId || undefined,
    validatedAnalysisId ? shortId : undefined,
  );
  const ogTitle = analysisData?.title || 'Drama Analysis - DramaDetector';

  if (isLoading) {
    return (
      <>
        <SharedLinkHeader shortId={shortId} analysisData={analysisData} />
        <Stack.Screen options={{ title: ogTitle, headerShown: false }} />
        <SharedLinkLoading />
      </>
    );
  }

  if (analysisError || !analysis) {
    const errorType = analysisError?.message?.toLowerCase().includes('expired') ? 'expired'
      : analysisError?.message?.toLowerCase().includes('not found') ? 'not_found'
      : 'unknown';

    return (
      <>
        <SharedLinkHeader shortId={shortId} analysisData={analysisData} />
        <Stack.Screen options={{ title: ogTitle, headerShown: false }} />
        <SharedLinkError error={errorType} onNewAnalysis={onNewAnalysis} />
      </>
    );
  }

  return (
    <>
      <SharedLinkHeader shortId={shortId} analysisData={analysisData} />
      <Stack.Screen options={{ title: ogTitle, headerShown: false }} />
      <SharedLinkContent
        analysis={analysis}
        currentTier={currentTier}
        displayTier={displayTier}
        tierLevel={tierLevel}
        l3AccordionsEnabled={l3AccordionsEnabled}
        bottomPadding={bottomPadding}
        shortId={shortId}
        onNewAnalysis={onNewAnalysis}
        onUpgrade={onUpgrade}
        t={t}
      />
    </>
  );
}
